<template>
  <div class="qr-code-generator">
    <!-- Direct QR Code Display -->
    <div v-if="showDirectly" class="text-center">
      <div v-if="qrCodeDataUrl" class="inline-block">
        <img :src="qrCodeDataUrl" alt="QR Code" class="max-w-full h-auto" />
      </div>
      <div v-else class="p-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p class="text-gray-600 mt-2 text-sm">QR Kod oluşturuluyor...</p>
      </div>
    </div>

    <!-- QR Code Button -->
    <button
      v-else
      @click="showQRModal = true"
      class="px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors text-sm"
      :title="`Generate QR Code for ${app.name}`"
    >
      <i class="fas fa-qrcode mr-1"></i>
      QR Code
    </button>

    <!-- QR Code Modal -->
    <div
      v-if="showQRModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click="closeModal"
    >
      <div
        class="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-auto"
        @click.stop
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-800">QR Code for {{ app.name }}</h3>
          <button
            @click="closeModal"
            class="text-gray-500 hover:text-gray-700 text-xl"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- QR Code Display -->
        <div class="text-center mb-4">
          <div v-if="qrCodeDataUrl" class="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
            <img :src="qrCodeDataUrl" alt="QR Code" class="max-w-full h-auto" />
          </div>
          <div v-else class="p-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p class="text-gray-600 mt-2">Generating QR Code...</p>
          </div>
        </div>

        <!-- App Info -->
        <div class="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 class="font-medium text-gray-800 mb-2">App Information</h4>
          <div class="space-y-1 text-sm text-gray-600">
            <div><strong>Name:</strong> {{ app.name }}</div>
            <div><strong>App ID:</strong> {{ app.id }}</div>
            <div class="flex items-center">
              <strong>Status:</strong>
              <span class="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                <i class="fas fa-globe mr-1"></i>
                Published
              </span>
            </div>
          </div>
        </div>

        <!-- Instructions -->
        <div class="bg-blue-50 rounded-lg p-4 mb-4">
          <h4 class="font-medium text-blue-800 mb-2">
            <i class="fas fa-info-circle mr-2"></i>
            How to Use
          </h4>
          <ol class="text-sm text-blue-700 space-y-1">
            <li>1. Scan this QR code with any QR code reader</li>
            <li>2. The QR code will open the app directly in your web browser</li>
            <li>3. Or use the mobile app to scan and add to your app list</li>
            <li>4. The QR code works on both web and mobile platforms</li>
          </ol>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-2">
          <button
            @click="downloadQRCode"
            class="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            :disabled="!qrCodeDataUrl"
          >
            <i class="fas fa-download mr-2"></i>
            Download QR Code
          </button>
          <button
            @click="shareApp"
            class="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <i class="fas fa-share mr-2"></i>
            Share Link
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue'
import QRCode from 'qrcode'
import type { App } from '../types/api'

interface Props {
  app: App
  showDirectly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showDirectly: false
})

const showQRModal = ref(false)
const qrCodeDataUrl = ref<string | null>(null)

// Generate QR code when modal is opened
watch(showQRModal, async (newValue) => {
  if (newValue) {
    await generateQRCode()
  }
})

// Generate QR code immediately if showDirectly is true
onMounted(() => {
  if (props.showDirectly) {
    generateQRCode()
  }
})

const generateQRCode = async (): Promise<void> => {
  try {
    qrCodeDataUrl.value = null
    
    // Create QR code data with direct web link to the published app
    const appUrl = `${window.location.origin}/app/${props.app.id}`
    
    // Generate QR code as data URL
    const qrCodeUrl = await QRCode.toDataURL(appUrl, {
      width: 256,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      errorCorrectionLevel: 'M'
    })
    
    qrCodeDataUrl.value = qrCodeUrl
  } catch (error) {
    console.error('Error generating QR code:', error)
  }
}

const closeModal = (): void => {
  showQRModal.value = false
  qrCodeDataUrl.value = null
}

const downloadQRCode = (): void => {
  if (!qrCodeDataUrl.value) return
  
  // Create download link
  const link = document.createElement('a')
  link.download = `${props.app.name}-qr-code.png`
  link.href = qrCodeDataUrl.value
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const shareApp = async (): Promise<void> => {
  const appUrl = `${window.location.origin}/app/${props.app.id}`
  
  try {
    if (navigator.share) {
      await navigator.share({
        title: props.app.name,
        text: `Check out this public file repository: ${props.app.name}`,
        url: appUrl
      })
    } else {
      // Fallback to clipboard
      await navigator.clipboard.writeText(appUrl)
      alert('App URL copied to clipboard!')
    }
  } catch (error) {
    console.error('Error sharing app:', error)
    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(appUrl)
      alert('App URL copied to clipboard!')
    } catch (clipboardError) {
      console.error('Error copying to clipboard:', clipboardError)
    }
  }
}
</script>

<style scoped>
.qr-code-generator {
  display: inline-block;
}

/* Modal backdrop blur effect */
.fixed.inset-0 {
  backdrop-filter: blur(4px);
}

/* QR Code image styling */
img {
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: pixelated;
}
</style> 